/**
 * 途经点步骤配置
 *
 * Tab（展示哪些途经点）和 Step（每个途经点有哪些操作节点）统一由此文件配置。
 *
 * 配置规则：
 * - 每条配置指定 routeType(路线类型) + waypointType(途经点类型) → steps(步骤列表)
 * - 可选 businessType(业务类型) 字段，用于覆盖特定业务类型的步骤
 * - 查找时优先匹配带 businessType 的配置，未命中则回退到不带 businessType 的默认配置
 * - simplified（命中仓库操作简化，由后端判定）为 true 时覆盖途经点 / 终点步骤，优先级最高
 */
import { WaypointTypeEnum, TmsRouteTypeEnum, TmsBusinessTypeEnum } from '../../../../enums/TmsEnum';

/** 步骤类型 */
export enum StepKey {
    DriverArrived = 'driverArrived',
    Loading = 'loading',
    Sealing = 'sealing',
    Departure = 'departure',
    Unsealing = 'unsealing',
    Unloading = 'unloading',
}

/** 步骤配置 */
export interface StepConfig {
    key: StepKey;
    labelKey: string;
}

/** 步骤类型对应的 i18n 键 */
export const STEP_LABEL_KEYS: Record<StepKey, string> = {
    [StepKey.DriverArrived]: 'tms.task.waypointStep.driverArrived',
    [StepKey.Loading]: 'tms.task.waypointStep.loading',
    [StepKey.Sealing]: 'tms.task.waypointStep.sealing',
    [StepKey.Departure]: 'tms.task.waypointStep.departure',
    [StepKey.Unsealing]: 'tms.task.waypointStep.unsealing',
    [StepKey.Unloading]: 'tms.task.waypointStep.unloading',
};

// ====== 步骤积木块 ======
const ORIGIN_STEPS: StepKey[] = [StepKey.DriverArrived, StepKey.Loading, StepKey.Sealing, StepKey.Departure];
const WAYPOINT_FULL: StepKey[] = [StepKey.DriverArrived, StepKey.Unsealing, StepKey.Unloading, StepKey.Loading, StepKey.Sealing, StepKey.Departure];
const DESTINATION_FULL: StepKey[] = [StepKey.DriverArrived, StepKey.Unsealing, StepKey.Unloading];
const DESTINATION_ARRIVAL_ONLY: StepKey[] = [StepKey.DriverArrived];
const WAYPOINT_ARRIVAL_DEPARTURE: StepKey[] = [StepKey.DriverArrived, StepKey.Departure];

// ====== 步骤配置表 ======
interface StepRule {
    routeType: number;
    businessType?: number;
    waypointType: number;
    steps: StepKey[];
}

const STEP_CONFIG: StepRule[] = [
    // 交接（所有 businessType）
    { routeType: TmsRouteTypeEnum.Handover,  waypointType: WaypointTypeEnum.Origin,      steps: ORIGIN_STEPS },

    // 干线（默认）
    { routeType: TmsRouteTypeEnum.Linehaul,  waypointType: WaypointTypeEnum.Origin,      steps: ORIGIN_STEPS },
    { routeType: TmsRouteTypeEnum.Linehaul,  waypointType: WaypointTypeEnum.Waypoint,    steps: WAYPOINT_FULL },
    { routeType: TmsRouteTypeEnum.Linehaul,  waypointType: WaypointTypeEnum.Destination, steps: DESTINATION_FULL },
    // 干线 - TemuGA/SCF 终点站覆盖
    { routeType: TmsRouteTypeEnum.Linehaul,  businessType: TmsBusinessTypeEnum.TemuGA,  waypointType: WaypointTypeEnum.Destination, steps: DESTINATION_ARRIVAL_ONLY },
    { routeType: TmsRouteTypeEnum.Linehaul,  businessType: TmsBusinessTypeEnum.TemuSCF, waypointType: WaypointTypeEnum.Destination, steps: DESTINATION_ARRIVAL_ONLY },

    // 支线（默认）
    { routeType: TmsRouteTypeEnum.Feeder,    waypointType: WaypointTypeEnum.Origin,      steps: ORIGIN_STEPS },
    { routeType: TmsRouteTypeEnum.Feeder,    waypointType: WaypointTypeEnum.Waypoint,    steps: WAYPOINT_FULL },
    { routeType: TmsRouteTypeEnum.Feeder,    waypointType: WaypointTypeEnum.Destination, steps: DESTINATION_FULL },
    // 支线 - TemuGA/SCF 终点站覆盖
    { routeType: TmsRouteTypeEnum.Feeder,    businessType: TmsBusinessTypeEnum.TemuGA,  waypointType: WaypointTypeEnum.Destination, steps: DESTINATION_ARRIVAL_ONLY },
    { routeType: TmsRouteTypeEnum.Feeder,    businessType: TmsBusinessTypeEnum.TemuSCF, waypointType: WaypointTypeEnum.Destination, steps: DESTINATION_ARRIVAL_ONLY },
];

/**
 * 根据 routeType + waypointType + businessType 解析步骤列表。
 * simplified（命中仓库操作简化，由后端判定）优先级最高：起点不变，途经点仅到达/发车，终点仅到达；
 * 其次匹配带 businessType 的配置，未命中则回退到不带 businessType 的默认配置。
 */
export function resolveSteps(waypointType: number, routeType?: number, businessType?: number, simplified?: boolean): StepKey[] {
    if (routeType == null) return [];

    // 仓库操作简化：是否支线由后端判定收口，前端只认标记
    if (simplified) {
        if (waypointType === WaypointTypeEnum.Waypoint) return WAYPOINT_ARRIVAL_DEPARTURE;
        if (waypointType === WaypointTypeEnum.Destination) return DESTINATION_ARRIVAL_ONLY;
    }

    // 优先：匹配 routeType + businessType + waypointType
    if (businessType != null) {
        const specific = STEP_CONFIG.find(r => r.routeType === routeType && r.businessType === businessType && r.waypointType === waypointType);
        if (specific) return specific.steps;
    }

    // 回退：匹配 routeType + waypointType（无 businessType）
    const fallback = STEP_CONFIG.find(r => r.routeType === routeType && r.businessType == null && r.waypointType === waypointType);
    return fallback?.steps ?? [];
}
